import { Button, makeStyles } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import SettingsList from "./SettingsList";
import ResourceManagementComponentsTable from "./ResourceManagementComponentsTable";
import ResourceManagementFields from "./ResourceManagementFields";
import {
  getSkills,
  getAdminDepartments,
  getRoles,
  getRMFieldSettings,
  updateRMFieldSettings,
} from "../../../actions/admin-actions";
import {
  getDepartmentsResources,
  getResourceHierarchy,
} from "../../../actions/resource-management-actions";
import Loading from "../../../common-components/Loading";
import ActionMessages from "../../../common-components/ActionMessages";
import { getResponse } from "../../../shared-resources/utils";
import DepartmentHierarchy from "./DepartmentHierarchy";
import HierarchyContainer from "./HierarchyContainer";
import Delegations from "./Delegations";

const useStyles = makeStyles((theme) => ({
  updateBtn: {
    fontSize: "14px",
    padding: "4px 12px !important",
    textTransform: "none",
    borderRadius: "20px",
    background: "#0070D2",
    marginBottom: "12px",
  },
}));

export default function ResourceManagement({ page }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [selectedPage, setSelectedPage] = useState("Main");
  const [skillsTree, setSkillsTree] = useState([]);
  const [rolesTree, setRolesTree] = useState([]);
  const [departmentsTree, setDepartmentsTree] = useState([]);
  const [departmentsResources, setDepartmentResources] = useState([]);
  const [resourceHierarchy, setResourceHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyErrMsg, setEmptyErrMsg] = useState(false);
  const [msgType, setMsgType] = useState();
  const [msgText, setMsgText] = useState();
  const [componentsSettingsData, setComponentsSettingsData] = useState([]);
  const [fieldsSettingsData, setFieldsSettingsData] = useState([]);
  const [buttonsSettingsData, setButtonsSettingsData] = useState([]);
  const [updateData, setUpdateData] = useState([]);
  const [isUpdateEnable, setIsUpdateEnable] = useState(false);
  const [isUpdateClick, setIsUpdateClick] = useState(false);

  const skillsData = useSelector((state) => state.adminReducer.skillsData);

  const rolesData = useSelector((state) => state.adminReducer.rolesData);

  const departmentsData = useSelector(
    (state) => state.adminReducer.adminDepartmentsData
  );
  const departmentsResourcesData = useSelector(
    (state) => state.resourceManagementReducer.departmentsResourcesData
  );
  const resourceHierarchyData = useSelector(
    (state) => state.resourceManagementReducer.resourceHierarchyData
  );

  const rmFieldSettingsData = useSelector(
    (state) => state.adminReducer.rmFieldSettingsData
  );

  const updateRMFieldSettingsData = useSelector(
    (state) => state.adminReducer.updateRMFieldSettingsData
  );

  const compare = (a, b) => {
    if (a.field_id < b.field_id) {
      return -1;
    }
    if (a.field_id > b.field_id) {
      return 1;
    }
    return 0;
  };

  useEffect(() => {
    const childSelected = page.children.filter((val) => val.isSelected);
    if (childSelected.length > 0) {
      setSelectedPage(childSelected[0].page);
    } else setSelectedPage("Main");
  }, [page]);

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getAdminDepartments());
    dispatch(getDepartmentsResources());
    dispatch(getResourceHierarchy());
    dispatch(getSkills());
    dispatch(getRMFieldSettings());
  }, []);

  const isClosed = (val) => {
    setEmptyErrMsg(!val);
  };

  useEffect(() => {
    if (
      rmFieldSettingsData &&
      rmFieldSettingsData.status &&
      (rmFieldSettingsData.status === 200 ||
        rmFieldSettingsData.status === 400) &&
      rolesData.status &&
      (rolesData.status === 200 || rolesData.status === 400) &&
      skillsData.status &&
      (skillsData.status === 200 || skillsData.status === 400) &&
      departmentsData.status &&
      (departmentsData.status === 200 || departmentsData.status === 400)
    ) {
      setLoading(false);
    }
    if (
      skillsData.status &&
      (skillsData.status === 200 || skillsData.status === 400)
    ) {
      setSkillsTree(getResponse(skillsData, []));
    }
    if (
      rolesData.status &&
      (rolesData.status === 200 || rolesData.status === 400)
    ) {
      setRolesTree(getResponse(rolesData, []));
    }
    if (
      departmentsResourcesData.status &&
      (departmentsResourcesData.status === 200 ||
        departmentsResourcesData.status === 400)
    ) {
      setDepartmentResources(getResponse(departmentsResourcesData, []));
    }
    if (
      resourceHierarchyData.status &&
      (resourceHierarchyData.status === 200 ||
        resourceHierarchyData.status === 400)
    ) {
      setResourceHierarchy(getResponse(resourceHierarchyData, []));
    }
    if (
      departmentsData.status &&
      (departmentsData.status === 200 || departmentsData.status === 400)
    ) {
      setDepartmentsTree(getResponse(departmentsData, []));
      if (
        rmFieldSettingsData &&
        rmFieldSettingsData.status &&
        (rmFieldSettingsData.status === 200 ||
          rmFieldSettingsData.status === 400)
      ) {
        const rmSetData = getResponse(rmFieldSettingsData);
        if (rmSetData) {
          if (rmSetData.componentSettings)
            setComponentsSettingsData(
              rmSetData.componentSettings.sort(compare)
            );
          if (rmSetData.fieldSettings)
            setFieldsSettingsData(rmSetData.fieldSettings.sort(compare));
          if (rmSetData.buttonSettings)
            setButtonsSettingsData(rmSetData.buttonSettings.sort(compare));
          setUpdateData(rmSetData);
        }
      }
    }
  }, [skillsData, rolesData, departmentsData, rmFieldSettingsData]);

  const getTreeData = () => {
    if (selectedPage === "Skills") return skillsTree;
    else if (selectedPage === "Roles") return rolesTree;
    else return departmentsTree;
  };

  const getPicklistLength = (items, value) => {
    return items.filter(
      (val) =>
        val.name.toLowerCase().trim().replace(/\s\s+/g, " ") ===
        value.toLowerCase().trim().replace(/\s\s+/g, " ")
    ).length;
  };

  const onUpdateClick = () => {
    let isValid = true;
    let isNotDuplicate = true;
    let emptyFields = [];
    updateData.fieldSettings.forEach((val) => {
      if (val.adminPicklistSettingsList && isValid) {
        val.adminPicklistSettingsList.forEach((item) => {
          if (item.name.toString().trim() === "") {
            isValid = false;
            emptyFields.push(val.fields);
          }
        });
      }
      if (
        val.setting_type === 3 &&
        (val.resource_type === "Picklist" ||
          val.resource_type === "Picklist-Multiselect") &&
        val.adminPicklistSettingsList.length === 0
      ) {
        isValid = false;
        emptyFields.push(val.fields);
      }
      if (val.adminPicklistSettingsList && isNotDuplicate) {
        val.adminPicklistSettingsList.forEach((item) => {
          isNotDuplicate =
            getPicklistLength(val.adminPicklistSettingsList, item.name) > 1
              ? false
              : true;
        });
      }
    });
    if (!isValid) {
      setEmptyErrMsg(true);
      setMsgType("error");
      setMsgText(
        `Field type Picklist/Picklist-Multiselect does not have any picklist values: ${emptyFields.join(
          ", "
        )}`
      );
    } else if (!isNotDuplicate) {
      setEmptyErrMsg(true);
      setMsgType("error");
      setMsgText("Duplicate picklist values are not allowed");
    } else {
      setLoading(true);
      setIsUpdateClick(true);
      dispatch(updateRMFieldSettings(updateData));
    }
  };

  useEffect(() => {
    if (
      updateRMFieldSettingsData &&
      updateRMFieldSettingsData.status &&
      updateRMFieldSettingsData.status === 200 &&
      isUpdateClick
    ) {
      setLoading(false);
      setEmptyErrMsg(true);
      setMsgType("success");
      setMsgText("Settings updated successfully");
      setIsUpdateEnable(false);
      setIsUpdateClick(false);
      dispatch(getRMFieldSettings());
    }
    if (
      updateRMFieldSettingsData &&
      updateRMFieldSettingsData.status &&
      updateRMFieldSettingsData.status === 400 &&
      isUpdateClick
    ) {
      setLoading(false);
      setEmptyErrMsg(true);
      setMsgType("error");
      setIsUpdateClick(false);
      setMsgText(updateRMFieldSettingsData.message);
    }
  }, [updateRMFieldSettingsData]);

  const removeEditFlag = (data) => {
    const changeData = JSON.parse(JSON.stringify(data));
    changeData.forEach((item) => {
      item.adminPicklistSettingsList.forEach((val) => {
        delete val.isEditable;
      });
    });
    return changeData;
  };

  const changeComponentsTable = (data) => {
    setUpdateData({ ...updateData, componentSettings: data });
    setIsUpdateEnable(true);
  };

  const changePrimaryTable = (data) => {
    const excludeFlag = removeEditFlag(JSON.parse(JSON.stringify(data)));
    setUpdateData({ ...updateData, fieldSettings: excludeFlag });
    setIsUpdateEnable(true);
  };

  const changeSecondaryable = (data) => {
    setUpdateData({ ...updateData, buttonSettings: data });
    setIsUpdateEnable(true);
  };

  return (
    <div>
      <div style={{ textAlign: "right" }}>
        {selectedPage === "Main" && (
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            className={classes.updateBtn}
            onClick={onUpdateClick}
            disabled={!isUpdateEnable}
          >
            Update
          </Button>
        )}
      </div>
      {selectedPage === "Main" ? (
        <>
          <ResourceManagementComponentsTable
            data={componentsSettingsData}
            changeComponentsTable={changeComponentsTable}
          />
          <ResourceManagementFields
            resourceManagementPermissions={fieldsSettingsData}
            resourceManagementPermissionsSecondary={buttonsSettingsData}
            changePrimaryTable={changePrimaryTable}
            changeSecondaryable={changeSecondaryable}
          />
        </>
      ) : selectedPage === "Hierarchy" ? (
        <HierarchyContainer
          nodesData={departmentsTree}
          departmentsResources={departmentsResources}
          resourceHierarchy={resourceHierarchy}
        />
      ) : selectedPage === "Delegations" ? (
        <Delegations />
      ) : (
        <SettingsList
          data={getTreeData()}
          departmentsList={departmentsTree}
          type={selectedPage}
        />
      )}
      {loading && <Loading />}
      {emptyErrMsg && (
        <ActionMessages type={msgType} isClosed={isClosed} message={msgText} />
      )}
    </div>
  );
}
